/**
 * The simulation's transcendental math, the same bits in every engine (ADR-0233). JS's `+ − × ÷` and `Math.sqrt` are
 * correctly rounded by the spec, but `Math.sin`, `cos`, `atan2`, `hypot`, `exp`, `log`, `pow` and `**` are each
 * engine's own approximation: V8 and JavaScriptCore disagree in the last bit, V8's versions disagree among themselves,
 * so a tape replayed in a browser drifted within seconds. These are fdlibm's (FreeBSD msun's) algorithms written in
 * those correctly rounded operations alone, within an ulp or two of the true value: a pure package calls them, never
 * `Math`'s (Biome: `tools/lint/engine-math.grit`).
 */

// Sine and cosine on [-π/4, π/4] (FreeBSD k_sin.c, k_cos.c).
const S1 = -0.16666666666666632;
const S2 = 0.00833333333332249;
const S3 = -0.0001984126982985795;
const S4 = 0.0000027557313707070068;
const S5 = -2.5050760253406863e-8;
const S6 = 1.58969099521155e-10;
const C1 = 0.0416666666666666;
const C2 = -0.001388888888887411;
const C3 = 0.00002480158728947673;
const C4 = -2.7557314351390663e-7;
const C5 = 2.087572321298175e-9;
const C6 = -1.1359647557788195e-11;

/** sin(x + y) for |x| ≤ π/4, y the tail of x's reduction. */
function ksin(x: number, y: number, tail: boolean): number {
  const z = x * x;
  const w = z * z;
  const r = S2 + z * (S3 + z * S4) + z * w * (S5 + z * S6);
  const v = z * x;
  return tail ? x - (z * (0.5 * y - v * r) - y - v * S1) : x + v * (S1 + z * r);
}

/** cos(x + y) for |x| ≤ π/4. */
function kcos(x: number, y: number): number {
  const z = x * x;
  const w = z * z;
  const r = z * (C1 + z * (C2 + z * C3)) + w * w * (C4 + z * (C5 + z * C6));
  const hz = 0.5 * z;
  const q = 1 - hz;
  return q + (1 - q - hz + (z * r - x * y));
}

// x mod π/2 (FreeBSD e_rem_pio2.c's medium case, both rounds always: π/2 in three parts, good to 118 bits). Past
// 2^20·π/2 an angle is first taken mod the double nearest 2π (`%` is exact by the spec): finite and the same bits
// everywhere, but no longer accurate. No angle in a game comes near.
const INV_PIO2 = 0.6366197723675814;
const PIO2_1 = 1.5707963267341256;
const PIO2_2 = 6.077100506303966e-11;
const PIO2_2T = 2.0222662487959506e-21;
const QUARTER_PI = 0.7853981633974483;
const MEDIUM = 1647099.3291652855;
const TWO_PI = 6.283185307179586;
/** The reduction's result, hi and lo (no allocation). */
let R0 = 0;
let R1 = 0;

/** Reduces `x` to R0 + R1 in [-π/4, π/4] and returns its quadrant. */
function reduce(v: number): number {
  const x = v > -MEDIUM && v < MEDIUM ? v : v % TWO_PI;
  const fn = Math.round(x * INV_PIO2);
  const t = x - fn * PIO2_1;
  const w = fn * PIO2_2;
  const r = t - w;
  const tail = fn * PIO2_2T - (t - r - w);
  R0 = r - tail;
  R1 = r - R0 - tail;
  return fn & 3;
}

export function sin(x: number): number {
  if (x > -QUARTER_PI && x < QUARTER_PI)
    return x > -1.4901161193847656e-8 && x < 1.4901161193847656e-8 ? x : ksin(x, 0, false);
  if (!Number.isFinite(x)) return Number.NaN;
  const n = reduce(x);
  return n === 0 ? ksin(R0, R1, true) : n === 1 ? kcos(R0, R1) : n === 2 ? -ksin(R0, R1, true) : -kcos(R0, R1);
}

export function cos(x: number): number {
  if (x > -QUARTER_PI && x < QUARTER_PI) return kcos(x, 0);
  if (!Number.isFinite(x)) return Number.NaN;
  const n = reduce(x);
  return n === 0 ? kcos(R0, R1) : n === 1 ? -ksin(R0, R1, true) : n === 2 ? -kcos(R0, R1) : ksin(R0, R1, true);
}

// atan (fdlibm s_atan.c): atan(a) for a ≥ 0, from the nearest of 0, atan(1/2), atan(1), atan(3/2), atan(∞).
const ATAN_HI = [0.4636476090008061, 0.7853981633974483, 0.982793723247329, 1.5707963267948966];
const ATAN_LO = [2.2698777452961687e-17, 3.061616997868383e-17, 1.3903311031230998e-17, 6.123233995736766e-17];
const T0 = 0.3333333333333293;
const T1 = -0.19999999999876483;
const T2 = 0.14285714272503466;
const T3 = -0.11111110405462356;
const T4 = 0.09090887133436507;
const T5 = -0.0769187620504483;
const T6 = 0.06661073137387531;
const T7 = -0.058335701337905735;
const T8 = 0.049768779946159324;
const T9 = -0.036531572744216916;
const T10 = 0.016285820115365782;

function atanAbs(a: number): number {
  let id = -1;
  let x = a;
  if (a >= 0.4375) {
    if (a < 1.1875) {
      if (a < 0.6875) {
        id = 0;
        x = (2 * a - 1) / (2 + a);
      } else {
        id = 1;
        x = (a - 1) / (a + 1);
      }
    } else if (a < 2.4375) {
      id = 2;
      x = (a - 1.5) / (1 + 1.5 * a);
    } else {
      id = 3;
      x = -1 / a;
    }
  }
  const z = x * x;
  const w = z * z;
  const s1 = z * (T0 + w * (T2 + w * (T4 + w * (T6 + w * (T8 + w * T10)))));
  const s2 = w * (T1 + w * (T3 + w * (T5 + w * (T7 + w * T9))));
  return id < 0 ? x - x * (s1 + s2) : (ATAN_HI[id] as number) - (x * (s1 + s2) - (ATAN_LO[id] as number) - x);
}

const PI = Math.PI;
const PI_LO = 1.2246467991473532e-16;
const negative = (v: number): boolean => v < 0 || (v === 0 && 1 / v < 0);

/** atan2 as `Math.atan2` defines it, signed zeros and infinities included (fdlibm e_atan2.c). */
export function atan2(y: number, x: number): number {
  if (Number.isNaN(x) || Number.isNaN(y)) return Number.NaN;
  if (y === 0) return negative(x) ? (negative(y) ? -PI : PI) : y;
  if (x === 0) return y > 0 ? PI / 2 : -PI / 2;
  const xInf = x === Number.POSITIVE_INFINITY || x === Number.NEGATIVE_INFINITY;
  const yInf = y === Number.POSITIVE_INFINITY || y === Number.NEGATIVE_INFINITY;
  if (xInf) {
    const v = yInf ? (x > 0 ? QUARTER_PI : 3 * QUARTER_PI) : x > 0 ? 0 : PI;
    return y > 0 ? v : -v;
  }
  if (yInf) return y > 0 ? PI / 2 : -PI / 2;
  const z = atanAbs(Math.abs(y / x));
  return x > 0 ? (y > 0 ? z : -z) : y > 0 ? PI - (z - PI_LO) : z - PI_LO - PI;
}

/** asin through atan2: within a couple of ulps, NaN outside [-1, 1]. */
export const asin = (x: number): number => atan2(x, Math.sqrt((1 - x) * (1 + x)));

/** √(x² + y² + z²), the operations in this order. No guard against overflow past 1e154: nothing in a game is near. */
export const hypot = (x: number, y: number, z = 0): number => Math.sqrt(x * x + y * y + z * z);

/** x², for what was `x ** 2`. */
export const sq = (x: number): number => x * x;

// exp (fdlibm e_exp.c): x = k·ln2 + r, |r| ≤ ln2/2, e^r by a rational fit, scaled by 2^k.
const LN2_HI = 0.6931471803691238;
const LN2_LO = 1.9082149292705877e-10;
const INV_LN2 = Math.LOG2E;
const P1 = 0.16666666666666602;
const P2 = -0.0027777777777015593;
const P3 = 0.00006613756321437934;
const P4 = -0.0000016533902205465252;
const P5 = 4.1381367970572385e-8;
const TWO_M1000 = 9.332636185032189e-302;

/** 2^k for an integer |k| ≤ 1023, exactly (powers of two multiply exactly). */
function pow2(k: number): number {
  let r = 1;
  let b = k < 0 ? 0.5 : 2;
  for (let n = Math.abs(k); n > 0; n >>= 1) {
    if (n & 1) r *= b;
    b *= b;
  }
  return r;
}

export function exp(x: number): number {
  if (Number.isNaN(x)) return x;
  if (x > 709.782712893384) return Number.POSITIVE_INFINITY;
  if (x < -745.1332191019411) return 0;
  const a = Math.abs(x);
  if (a < 3.725290298461914e-9) return 1 + x;
  let k = 0;
  let hi = x;
  let lo = 0;
  if (a > 0.34657359027997264) {
    k = a < 1.0397207708399179 ? (x > 0 ? 1 : -1) : Math.trunc(INV_LN2 * x + (x > 0 ? 0.5 : -0.5));
    hi = x - k * LN2_HI;
    lo = k * LN2_LO;
  }
  const r = hi - lo;
  const t = r * r;
  const c = r - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
  if (k === 0) return 1 - ((r * c) / (c - 2) - r);
  const y = 1 - (lo - (r * c) / (2 - c) - hi);
  if (k > 1023) return y * 2 * pow2(k - 1);
  return k >= -1021 ? y * pow2(k) : y * pow2(k + 1000) * TWO_M1000;
}

// log (fdlibm e_log.c): x = 2^k · (1 + f), √2/2 < 1 + f < √2, log(1 + f) by s = f / (2 + f). It reads the double's
// words through a typed array: little-endian, as every machine that plays the game.
const LG1 = 0.6666666666666735;
const LG2 = 0.3999999999940942;
const LG3 = 0.2857142874366239;
const LG4 = 0.22222198432149784;
const LG5 = 0.1818357216161805;
const LG6 = 0.15313837699209373;
const LG7 = 0.14798198605116586;
const THIRD = 1 / 3;
const F64 = new Float64Array(1);
const I32 = new Int32Array(F64.buffer);

export function log(x: number): number {
  if (Number.isNaN(x) || x < 0) return Number.NaN;
  if (x === 0) return Number.NEGATIVE_INFINITY;
  if (x === Number.POSITIVE_INFINITY) return x;
  let k = 0;
  F64[0] = x;
  let hx = I32[1] as number;
  if (hx < 0x00100000) {
    // Subnormal: scale it up by 2^54.
    k -= 54;
    F64[0] = x * 18014398509481984;
    hx = I32[1] as number;
  }
  k += (hx >> 20) - 1023;
  hx &= 0x000fffff;
  const i = (hx + 0x95f64) & 0x100000;
  I32[1] = hx | (i ^ 0x3ff00000); // 1 + f, or (1 + f) / 2 past √2
  k += i >> 20;
  const f = (F64[0] as number) - 1;
  const dk = k;
  if ((0x000fffff & (2 + hx)) < 3) {
    // |f| < 2^-20.
    if (f === 0) return k === 0 ? 0 : dk * LN2_HI + dk * LN2_LO;
    const R = f * f * (0.5 - THIRD * f);
    return k === 0 ? f - R : dk * LN2_HI - (R - dk * LN2_LO - f);
  }
  const s = f / (2 + f);
  const z = s * s;
  const w = z * z;
  const t1 = w * (LG2 + w * (LG4 + w * LG6));
  const t2 = z * (LG1 + w * (LG3 + w * (LG5 + w * LG7)));
  const R = t2 + t1;
  if (((hx - 0x6147a) | (0x6b851 - hx)) > 0) {
    const hfsq = 0.5 * f * f;
    return k === 0 ? f - (hfsq - s * (hfsq + R)) : dk * LN2_HI - (hfsq - (s * (hfsq + R) + dk * LN2_LO) - f);
  }
  return k === 0 ? f - s * (f - R) : dk * LN2_HI - (s * (f - R) - dk * LN2_LO - f);
}

/**
 * x^y, for what was `Math.pow` or `**`. An integer exponent multiplies by squaring (as JavaScriptCore does, so Bun's
 * servers keep those bits); ½ is a square root; any other goes through exp(y·log x).
 * ponytail: that last is a few ulps off once |y·ln x| is large (fdlibm's pow carries log in extra precision: the
 * upgrade if a game ever needs one).
 */
export function pow(x: number, y: number): number {
  if (y === 0) return 1;
  if (Number.isInteger(y) && y >= -0x7fffffff && y <= 0x7fffffff) {
    let r = 1;
    let b = x;
    for (let n = Math.abs(y); n > 0; n >>>= 1) {
      if (n & 1) r *= b;
      b *= b;
    }
    return y < 0 ? 1 / r : r;
  }
  if (Number.isNaN(x) || Number.isNaN(y)) return Number.NaN;
  if (y === 0.5) return x === 0 ? 0 : x === Number.NEGATIVE_INFINITY ? Number.POSITIVE_INFINITY : Math.sqrt(x);
  if (x < 0 && Number.isInteger(y)) return y % 2 === 0 ? pow(-x, y) : -pow(-x, y);
  if (x < 0) return x === Number.NEGATIVE_INFINITY ? (y > 0 ? Number.POSITIVE_INFINITY : 0) : Number.NaN;
  if (x === 0) return y > 0 ? 0 : Number.POSITIVE_INFINITY;
  return exp(y * log(x));
}
